// Дашборд роли «Бухгалтер» — единая сводка вместо общего ClubDashboard
// (админского/владельческого). Собран целиком из уже существующих
// эндпоинтов бэкенда (ничего не выдумываем и не мокаем):
//   GET /v1/reports/period          — выручка/наличные/безнал за месяц
//   GET /v1/reports/shifts          — смены + инкассация за 30 дней
//   GET /v1/reports/fiscal-reconciliation — продажи без чека, проблемные операции
//   GET /v1/finance/invoices        — счета от Darkwood Soft (подписка)
//   GET /v1/reports/loyalty/liability — долг клуба по бонусам
//   GET /v1/journal                 — последние события клуба
//
// Секция «Скоро» — не запрос к API, а честная роадмап-заметка: показывает
// бухгалтеру, чего в системе пока нет (P&L мы сознательно не делаем — решение
// принято в пользу модуля зарплаты, см. дизайн-документ). Сверка фискализации
// и её собственная страница уже реализованы — не заглушка.

import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import type { FiscalReconciliationReport, PeriodReport, LoyaltyLiability } from '@billiard/shared'
import { useFetch } from '@/hooks/useApi'
import { AsyncState, EmptyState, Kpi, PageHead, SectionHead } from '@/components/ui'
import { money, count, dateTime, dateOnly } from '@/lib/format'
import { Cur, Money } from '@/lib/currency'

const DAY_MS = 24 * 60 * 60 * 1000

/** Начало текущего месяца в epoch-ms (локальная TZ). */
function startOfMonthMs(): number {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), 1, 0, 0, 0, 0).getTime()
}

// ── Локальные контракты ответов (зеркало бэкенда — те же поля, что в
//    Shifts.tsx/Finance.tsx; отдельно, т.к. общих типов в @billiard/shared нет) ──

interface ShiftListRow {
  id: string
  number: number
  openedAt: number
  closedAt: number | null
  openedByName: string | null
  revenue: number
  encashment: number
  cashTotal: number
  cardTotal: number
}

interface Invoice {
  id: string
  invoiceNumber: string
  purpose: string
  amountDue: number
  status: 'unpaid' | 'paid'
  paidAt: number | null
  createdAt: number
}

interface JournalRow {
  id: string
  ts: number
  kind: string
  message: string
  userName: string | null
}

export function AccountantOverview(): React.JSX.Element {
  return (
    <>
      <PageHead
        eyebrow="Обзор"
        title="Дашборд бухгалтера"
        lead="Выручка и оплаты за месяц, инкассация по сменам, счета от платформы и обязательства по
          бонусам — всё, что нужно закрыть период, в одном месте."
      />

      <MonthTotals />
      <ShiftsAttention />
      <FiscalReconciliationBlock />
      <SubscriptionInvoices />
      <LoyaltyLiabilityBlock />
      <RecentJournal />
      <RoadmapNote />
    </>
  )
}

// ═══════════════════════════ Выручка за месяц ═══════════════════════════

function MonthTotals(): React.JSX.Element {
  const from = startOfMonthMs()
  const to = Date.now()
  const period = useFetch<PeriodReport>('/v1/reports/period', { from, to }, [from])

  return (
    <>
      <SectionHead title="Текущий месяц" meta={`с ${dateOnly(from)} по ${dateOnly(to)}`} />
      <AsyncState query={period} loadingLabel="Считаем выручку за месяц…" />
      {period.data && (
        <div className="kpi-grid">
          <Kpi label="Выручка" value={<Money value={period.data.totals.revenue} />} gold accent />
          <Kpi label="Наличные" value={<Money value={period.data.totals.cash} />} />
          <Kpi label="Безналичные" value={<Money value={period.data.totals.card} />} />
          <Kpi label="Сессий" value={count(period.data.totals.count)} />
          <Kpi label="Скидки" value={<Money value={period.data.totals.discount} />} />
          <Kpi label="Бонусы списано" value={<Money value={period.data.totals.bonusUsed} />} />
        </div>
      )}
    </>
  )
}

// ═══════════════════════════ Смены и инкассация ═══════════════════════════

function ShiftsAttention(): React.JSX.Element {
  const now = Date.now()
  const from = now - 30 * DAY_MS
  const list = useFetch<ShiftListRow[]>('/v1/reports/shifts', { from, to: now }, [from])
  const rows = list.data ?? []

  // «Требуют внимания»: смена ещё открыта, либо закрыта, но инкассация меньше
  // наличных за смену (может быть нормой — часть наличных оставляют на размен
  // следующей смене, — поэтому это не ошибка, а повод свериться).
  const openShifts = rows.filter((r) => r.closedAt == null)
  const underEncashed = rows.filter(
    (r) => r.closedAt != null && r.cashTotal > 0 && r.encashment < r.cashTotal
  )
  const recent = rows.slice(0, 8)

  return (
    <>
      <SectionHead
        title="Смены и инкассация"
        meta={
          <>
            {count(rows.length)} за 30 дней · <Link to="/shifts">Все смены →</Link>
          </>
        }
      />
      <AsyncState
        query={list}
        isEmpty={rows.length === 0}
        empty="За последние 30 дней смен не было."
        loadingLabel="Загружаем смены…"
      />

      {rows.length > 0 && (
        <>
          {(openShifts.length > 0 || underEncashed.length > 0) && (
            <div className="kpi-grid" style={{ marginBottom: 4 }}>
              {openShifts.length > 0 && (
                <Kpi
                  label="Открытые смены"
                  value={count(openShifts.length)}
                  sub="Ещё не закрыты — итоги могут измениться"
                />
              )}
              {underEncashed.length > 0 && (
                <Kpi
                  label="Инкассация меньше наличных"
                  value={count(underEncashed.length)}
                  sub="Проверить: остаток в кассе или расхождение"
                />
              )}
            </div>
          )}

          <div className="table-wrap">
            <table className="rtable">
              <thead>
                <tr>
                  <th>Смена</th>
                  <th>Открыта</th>
                  <th>Закрыта</th>
                  <th className="num">Выручка</th>
                  <th className="num">Наличные</th>
                  <th className="num">Инкассация</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {recent.map((r) => {
                  const flagOpen = r.closedAt == null
                  const flagUnder = !flagOpen && r.cashTotal > 0 && r.encashment < r.cashTotal
                  return (
                    <tr key={r.id}>
                      <td>№{r.number}</td>
                      <td>{dateTime(r.openedAt)}</td>
                      <td>{r.closedAt ? dateTime(r.closedAt) : '—'}</td>
                      <td className="num">
                        {money(r.revenue)} <Cur />
                      </td>
                      <td className="num">
                        {money(r.cashTotal)} <Cur />
                      </td>
                      <td className="num">
                        {money(r.encashment)} <Cur />
                      </td>
                      <td>
                        {flagOpen && <span className="pill pill--open">Открыта</span>}
                        {flagUnder && (
                          <span
                            className="pill"
                            style={{
                              background: 'rgba(245,158,11,0.12)',
                              color: 'var(--amber)',
                              border: '1px solid rgba(245,158,11,0.3)'
                            }}
                          >
                            Сверить
                          </span>
                        )}
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  )
}

// ═══════════════════════════ Сверка фискализации ═══════════════════════════

function FiscalReconciliationBlock(): React.JSX.Element {
  const now = Date.now()
  const from = now - 30 * DAY_MS
  const report = useFetch<FiscalReconciliationReport>(
    '/v1/reports/fiscal-reconciliation',
    { from, to: now },
    [from]
  )
  const problems = report.data
    ? report.data.sessions.withoutDoc +
      report.data.cashOps.failed +
      report.data.cashOps.pending +
      report.data.refunds.failed +
      report.data.refunds.pending
    : 0

  return (
    <>
      <SectionHead
        title="Сверка фискализации"
        meta={<Link to="/reconciliation">Открыть сверку →</Link>}
      />
      <AsyncState query={report} loadingLabel="Сверяем последние 30 дней…" />
      {report.data && (
        <div className="kpi-grid">
          <Kpi label="Продаж без чека" value={count(report.data.sessions.withoutDoc)} />
          <Kpi
            label="Проблемных операций"
            value={count(report.data.cashOps.failed + report.data.cashOps.pending + report.data.refunds.failed + report.data.refunds.pending)}
            sub="Внесения/изъятия и возвраты без подтверждения кассы"
          />
          {problems === 0 && <Kpi label="Статус" value="Всё сходится" gold accent />}
        </div>
      )}
    </>
  )
}

// ═══════════════════════════ Счета от Darkwood Soft ═══════════════════════════

function SubscriptionInvoices(): React.JSX.Element {
  const list = useFetch<Invoice[]>('/v1/finance/invoices')
  const rows = list.data ?? []
  const unpaid = useMemo(() => rows.filter((r) => r.status === 'unpaid'), [rows])
  const unpaidSum = unpaid.reduce((a, r) => a + r.amountDue, 0)

  return (
    <>
      <SectionHead
        title="Счета от платформы"
        meta={
          <>
            {count(rows.length)} счёт(ов) · <Link to="/finance">Раздел «Финансы» →</Link>
          </>
        }
      />
      <AsyncState
        query={list}
        isEmpty={rows.length === 0}
        empty="Счетов пока нет."
        loadingLabel="Загружаем счета…"
      />

      {rows.length > 0 && (
        <>
          {unpaid.length > 0 && (
            <div className="kpi-grid" style={{ marginBottom: 4 }}>
              <Kpi
                label="Не оплачено"
                value={count(unpaid.length)}
                sub={
                  <>
                    на <Money value={unpaidSum} />
                  </>
                }
              />
            </div>
          )}
          <div className="table-wrap">
            <table className="rtable">
              <thead>
                <tr>
                  <th>№</th>
                  <th>Назначение</th>
                  <th className="num">Сумма</th>
                  <th>Статус</th>
                  <th>Оплачен</th>
                </tr>
              </thead>
              <tbody>
                {rows.slice(0, 6).map((inv) => (
                  <tr key={inv.id}>
                    <td>{inv.invoiceNumber}</td>
                    <td>{inv.purpose}</td>
                    <td className="num">
                      {money(inv.amountDue)} <Cur />
                    </td>
                    <td>
                      {inv.status === 'paid' ? (
                        <span className="pill pill--open">Оплачен</span>
                      ) : (
                        <span className="pill pill--closed">Не оплачен</span>
                      )}
                    </td>
                    <td>{inv.paidAt ? dateTime(inv.paidAt) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </>
  )
}

// ═══════════════════════════ Обязательства по бонусам ═══════════════════════════

function LoyaltyLiabilityBlock(): React.JSX.Element {
  const liability = useFetch<LoyaltyLiability>('/v1/reports/loyalty/liability')

  return (
    <>
      <SectionHead title="Программа лояльности" />
      <AsyncState query={liability} loadingLabel="Считаем баланс бонусов…" />
      {liability.data && (
        <div className="kpi-grid">
          <Kpi label="Клиентов с бонусами" value={count(liability.data.customers)} />
          <Kpi
            label="Долг по бонусам"
            value={<Money value={liability.data.totalBonusBalance} />}
            gold
            accent
            sub="Сумма непогашенных бонусных балансов клиентов"
          />
        </div>
      )}
    </>
  )
}

// ═══════════════════════════ Последние события журнала ═══════════════════════════

const FINANCE_KINDS = new Set(['shift', 'receipt', 'inventory'])

function RecentJournal(): React.JSX.Element {
  const now = Date.now()
  const [from] = useState(() => now - 7 * DAY_MS)
  const list = useFetch<JournalRow[]>('/v1/journal', { from, to: now, limit: 100 }, [from])
  const rows = (list.data ?? []).filter((r) => FINANCE_KINDS.has(r.kind)).slice(0, 8)

  return (
    <>
      <SectionHead
        title="Последние финансовые события"
        meta={<Link to="/journal">Весь журнал →</Link>}
      />
      <AsyncState
        query={list}
        isEmpty={rows.length === 0}
        empty="За последние 7 дней подходящих событий нет."
        loadingLabel="Загружаем журнал…"
      />
      {rows.length > 0 && (
        <div className="table-wrap">
          <table className="rtable">
            <thead>
              <tr>
                <th>Время</th>
                <th>Тип</th>
                <th>Сообщение</th>
                <th>Сотрудник</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id}>
                  <td>{dateTime(r.ts)}</td>
                  <td>{r.kind}</td>
                  <td>{r.message}</td>
                  <td>{r.userName || '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  )
}

// ═══════════════════════════ Роадмап (честная заглушка, без фейковых данных) ═══════════════════════════

function RoadmapNote(): React.JSX.Element {
  return (
    <>
      <SectionHead title="Скоро" meta="В разработке" />
      <EmptyState message="Экспорт в 1С/Excel — следующий шаг. P&L по клубу сознательно не делаем: без учёта расходов посчитать чистую прибыль всё равно нельзя. Зарплата по часам уже доступна в разделе «Зарплата»." />
    </>
  )
}
